import React from 'react';
import Button from '../../components/Button';
import FloatingCard from '../../components/FloatingCard';
import Popover from '../../components/Popover';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import { AnnotationPreviewContainer } from '../annotation/Annotation';
import AnnotationIcon from '../annotation/AnnotationIcon';
import AnnotationLink from '../annotation/AnnotationLink';
import AnnotationPreview from '../annotation/AnnotationPreview';
import isContentEmpty from '../utils/isContentEmpty';
import * as styles from './ExpandedPostModalAnnotation.module.scss';

const ANNOTATION_FRAGMENT = gql(`
  fragment ExpandedPostModalAnnotation_annotation on Annotation {
    id
    content
    link
    linkText
  }
`);

interface ExpandedPostModalAnnotationProps {
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>;
  compact: boolean;
  activeThreadId: string | null;
  onActiveThreadIdChange: (threadId: string | null) => void;
}

const ExpandedPostModalAnnotation = ({
  annotation,
  compact,
  activeThreadId,
  onActiveThreadIdChange,
}: ExpandedPostModalAnnotationProps) => {
  const sanitizedLink =
    annotation.link && sanitizeAnnotationLink(annotation.link);
  const trimmedLinkText = annotation.linkText?.trim() ?? '';

  const shouldShowLink = !isContentEmpty(annotation.content) && sanitizedLink;

  return (
    <div>
      <Popover
        placement="left-start"
        onOpenChange={(newIsOpen) => {
          onActiveThreadIdChange(newIsOpen ? annotation.id : null);
        }}
        flipBehavior="disabled"
        shiftBehavior="disabled"
        open={activeThreadId === annotation.id}
        view={
          <FloatingCard noArrow>
            <div className={styles.container}>
              <AnnotationPreviewContainer>
                <AnnotationPreview
                  annotation={annotation}
                  avatar={<AnnotationIcon />}
                />
              </AnnotationPreviewContainer>
              {shouldShowLink && (
                <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
              )}
            </div>
          </FloatingCard>
        }
        portalContainer="disabled"
      >
        {compact ? (
          <Button icon={<AnnotationIcon size="small" />} />
        ) : (
          <Button icon={<AnnotationIcon hoverable />} />
        )}
      </Popover>
    </div>
  );
};

export default ExpandedPostModalAnnotation;
